import React, { useState, useMemo, memo, useCallback } from "react";

import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import useMediaQuery from "use-mediaquery";
import routes from "routes";

import {
  closeConversationStart,
  updateUserAvatarStart,
} from "redux/chats/chats.actions";
import { CloseOutlineIconComponent } from "assets/icons";
import useTranslation from "hooks/useTranslation";
import Modal from "../../Modal";
import ModalBody from "../../Modal/ModalBody";
import ModalFooter from "../../Modal/ModalFooter";
import UserAvatar from "../UserAvatar";
import { verifyFileToOpen } from "utils/validation";
import ProgressBar from "components/ProgressBar";
import { ReactComponent as BlockedUserIcon } from "assets/icons/block_user.svg";
import { ReactComponent as BotAvatar } from "assets/icons/robotic_msg.svg";
import { getDecodedURL } from "utils/url";
import { truncate } from "utils/text";

const CONV_MODAL_INITIAL_STATE = {
  show: false,
  status: null,
};

const ALLOWED_PACKAGES_FOR_UPLOAD_AVATAR_FEAT = [
  "premium",
  "commerical",
  "trial",
  "premiumPlus",
];

// TODO: we already have a close conv modal component. we can use it  instead of duplicating
const UserItem = ({
  user,
  notSeenMessages,
  lastMessage,
  id,
  isPartnerChat,
  setConvBodyContent,
  selectedConversation,
  packageType,
  blockStatus,
  inChatWithBot,
  convType,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getTranslation } = useTranslation(["CHAT", "COMMON"]);
  const { t } = getTranslation();
  const [closeConvModalInfo, setCloseConvModalInfo] = useState(
    CONV_MODAL_INITIAL_STATE
  );
  const [isOnAvatar, setIsOnAvatar] = useState(false);
  const isSmallDevice = useMediaQuery("(max-width: 650px)");

  const isItemSelected =
    selectedConversation && selectedConversation.conversation.id === id;

  // do not show close button in otherConvs
  const showCloseButton = isItemSelected && convType !== "otherConvs";

  const shouldShowAvatarProgress = user?.progress && user?.progress !== null;
  const showNotSeenBadge = notSeenMessages > 0 && convType !== "otherConvs";
  const hasChannelDefaultPhoto = user?.avatar?.includes("channel-photos");

  const hasUploadAvatarPermission =
    ALLOWED_PACKAGES_FOR_UPLOAD_AVATAR_FEAT.includes(packageType);

  const handleUploadAvatar = useCallback(
    (filObj) => {
      const selectedFile = verifyFileToOpen(filObj);
      dispatch(updateUserAvatarStart(selectedFile, id, convType, user?.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, convType, user?.id]
  );

  const avatarHandler = useMemo(
    () =>
      hasUploadAvatarPermission &&
      selectedConversation &&
      selectedConversation.conversation.id === id
        ? handleUploadAvatar
        : undefined,

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasUploadAvatarPermission,
      selectedConversation?.conversation?.id,
      id,
      handleUploadAvatar,
    ]
  );

  const getLinkPath = () => {
    const queryParams = new URLSearchParams({
      ...(!!isPartnerChat && { isPartnerChat: true, userId: user.id }),
    }).toString();

    return `${routes.chats}/${id}?${queryParams}`;
  };

  const handleCloseConvModalDisplay = (status) => {
    setCloseConvModalInfo({
      show: status,
      status: null,
    });
  };

  const handleCloseConversation = (e) => {
    e.preventDefault();

    const status = convType === "ongoing" ? "close" : "denieded";
    setCloseConvModalInfo({
      show: true,
      status,
    });
  };

  const onSuccessfullyCloseConv = () => {
    if (isSmallDevice) {
      navigate(routes.convsList);
    } else {
      navigate(routes.home);
    }

    setCloseConvModalInfo(CONV_MODAL_INITIAL_STATE);
  };

  const closeConversation = () => {
    dispatch(
      closeConversationStart({
        action: closeConvModalInfo.status,
        userId: selectedConversation.user.id,
        convId: selectedConversation.conversation.id,
        onSuccess: onSuccessfullyCloseConv,
      })
    );

    if (isSmallDevice) navigate(routes.convsList);
    setCloseConvModalInfo(CONV_MODAL_INITIAL_STATE);
  };

  const handleCloseConvModalCancelBtnClick = () => {
    setCloseConvModalInfo({
      show: false,
      status: null,
    });
  };

  const renderCloseConvModal = () => {
    return (
      <Modal
        visible={closeConvModalInfo.show}
        setVisibility={handleCloseConvModalDisplay}
        className="confirmation-modal"
      >
        <ModalBody>
          <div>
            <div className="conv__body__header_block_user_modal_title">
              {t("MODAL").CLOSE_CONV_CONFIRM}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="modal__container__footer__left">
            <button
              onClick={handleCloseConvModalCancelBtnClick}
              className="btn btn-tertiary"
              id="closeModal"
            >
              {t("CANCEL")}
            </button>
            <button onClick={closeConversation} className="btn btn-primary">
              {t("CONFIRM")}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  const renderProgressBar = (
    <ProgressBar
      progress={user?.progress}
      trackWidth={5}
      indicatorWidth={5}
      size={56}
    />
  );

  const renderAvatar = () => {
    if (blockStatus) {
      return <BlockedUserIcon />;
    }

    if (user?.progress) {
      return renderProgressBar;
    }

    return (
      <UserAvatar
        acceptedFormats={"image/gif, image/jpeg, image/png"}
        avatar={user?.avatar}
        handleUploadAvatar={avatarHandler}
        renderProgress={renderProgressBar}
        showProgress={shouldShowAvatarProgress}
      />
    );
  };

  return user ? (
    <>
      {closeConvModalInfo.show && renderCloseConvModal()}

      <Link
        className="conv__list__inner__messagebox flex flex-ai-c"
        data-selected={isItemSelected}
        id="conversation-item"
        to={getLinkPath()}
      >
        <div
          onMouseEnter={() => setIsOnAvatar(true)}
          onMouseLeave={() => setIsOnAvatar(false)}
          className="conv__list__inner__messagebox__avatar_container"
        >
          {renderAvatar()}

          <span
            className={clsx(
              "status",
              user.isOnline ? "status-online" : "status-offline"
            )}
          ></span>
        </div>

        <div className="conv__list__inner__messagebox__text">
          <div className="conv__list__inner__messagebox__text__header flex flex-ai-c flex-jc-sb">
            <div className="conv__list__inner__messagebox__text__header__title">
              <span className="conv__list__inner__messagebox__text__header__title__label">
                {t("USER_TAG")} :
              </span>
              <span
                className="conv__list__inner__messagebox__text__header__title__value"
                title={user.fullName}
              >
                {truncate(user.fullName, 30)}
              </span>
            </div>

            <div className="conv__list__inner__messagebox__text__header__container">
              {showCloseButton && (
                <div
                  data-tool-tip-top={t("CLOSE_CONV")}
                  onClick={handleCloseConversation}
                  className="conv__list__inner__messagebox__text__header__close"
                >
                  <CloseOutlineIconComponent />
                </div>
              )}
            </div>
          </div>
          {user?.lastPage && (
            <div
              data-selected={isItemSelected}
              className="conv__list__inner__messagebox__channel"
            >
              <span>{getDecodedURL(user.lastPage)}</span>
            </div>
          )}

          <div className="conv__list__inner__messagebox__text__footer flex flex-ai-c flex-jc-sb">
            <div className="conv__list__inner__messagebox__text__footer__message">
              {inChatWithBot && (
                <span>
                  <BotAvatar />
                </span>
              )}
              {lastMessage && truncate(lastMessage, 30)}
            </div>

            {showNotSeenBadge && (
              <div className="conv__list__inner__messagebox__text__footer__badge">
                <div className="conv__list__inner__messagebox__text__footer__badge__inner flex flex-ai-c flex-jc-c">
                  {notSeenMessages}
                </div>
              </div>
            )}
          </div>
        </div>
      </Link>
    </>
  ) : (
    <></>
  );
};

// TODO: refactor propTypes
UserItem.propTypes = {
  id: PropTypes.string.isRequired,
  lastMessage: PropTypes.string.isRequired,
};

export default memo(UserItem);
